<section *ngIf="active" class="vc" aria-label="Plan it with me">

  <!-- What it just said: always a caption, spoken too unless muted. -->
  <div class="vc-head">
    <span class="vc-avatar" aria-hidden="true">
      <span *ngIf="speaking; else still" class="vc-eq"><i></i><i></i><i></i></span>
      <ng-template #still><ng-container *ngTemplateOutlet="micIcon16"></ng-container></ng-template>
    </span>
    <div class="vc-words" aria-live="polite">
      <p class="vc-caption">{{ caption }}</p>
      <p class="vc-heard" *ngIf="heard">
        “{{ heard }}”
        <span *ngIf="phase === 'thinking'" class="vc-dots" aria-label="Working it out"><i></i><i></i><i></i></span>
      </p>
    </div>
    <button type="button" class="vc-icon" (click)="toggleMute()"
      [attr.aria-pressed]="muted" [attr.aria-label]="muted ? 'Turn my voice on' : 'Turn my voice off'">
      <!-- Solar volume-cross / volume-loud, linear -->
      <svg *ngIf="muted" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 9.5v5h4l5 4v-13l-5 4h-4z" /><path d="M16.5 9.5l5 5M21.5 9.5l-5 5" /></g></svg>
      <svg *ngIf="!muted" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 9.5v5h4l5 4v-13l-5 4h-4z" /><path d="M16 9a4 4 0 0 1 0 6M18.5 6.5a7.5 7.5 0 0 1 0 11" /></g></svg>
    </button>
  </div>

  <!-- Quick answers -->
  <div *ngIf="chips.length && !isRecap" class="vc-chips" role="group" aria-label="Quick answers">
    <button *ngFor="let c of chips" type="button" class="vc-chip" [disabled]="busy" (click)="pickChip(c)">{{ c.label }}</button>
  </div>

  <!-- Cover photos for this kind of event -->
  <div *ngIf="step === 'cover' && phase !== 'thinking'" class="vc-scroll" role="group" aria-label="Cover photos">
    <button *ngFor="let c of covers; let i = index" type="button" class="vc-cover" [disabled]="busy"
      [attr.aria-label]="'Cover ' + (i + 1)" (click)="pickCover(c)">
      <img [src]="c.thumb" alt="" />
    </button>
    <button type="button" class="vc-cover vc-cover--upload" [disabled]="busy" (click)="uploadCover()">Upload your own</button>
    <button type="button" class="vc-cover vc-cover--skip" [disabled]="busy" (click)="skipCover()">Skip</button>
  </div>

  <!-- Typing -->
  <!-- A div, not a form: this panel sits inside the create form's own <form>. -->
  <div *ngIf="showTyping" class="vc-type">
    <input class="vc-input" type="text" [(ngModel)]="typed" [ngModelOptions]="{ standalone: true }"
      [placeholder]="typingPlaceholder" [attr.aria-label]="typingPlaceholder" maxlength="200" autocomplete="off"
      [disabled]="busy" (keydown.enter)="$event.preventDefault(); sendTyped()" />
    <button type="button" class="vc-send" aria-label="Send" [disabled]="busy || !typed.trim()" (click)="sendTyped()">
      <!-- Solar arrow-right-linear -->
      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M4 12h16m-6-6l6 6-6 6" /></svg>
    </button>
  </div>

  <!-- The mic -->
  <!-- Type · mic · I'll fill it in, with the hint under the mic. -->
  <div *ngIf="!isRecap" class="vc-mic-row">
    <span class="vc-side vc-side--start">
      <button *ngIf="canSpeak && !showTyping" type="button" class="vc-link" (click)="typingOpen = true">Type</button>
    </span>
    <button *ngIf="canSpeak" type="button" class="vc-mic" [class.is-listening]="phase === 'listening'"
      [disabled]="phase === 'thinking'" (click)="tapMic()"
      [attr.aria-label]="phase === 'listening' ? 'Done talking' : 'Answer out loud'">
      <span class="vc-mic-core">
        <span *ngIf="phase === 'listening'; else micIdle" class="vc-eq vc-eq--lg" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
        <ng-template #micIdle><ng-container *ngTemplateOutlet="micIcon24"></ng-container></ng-template>
      </span>
    </button>
    <span class="vc-side vc-side--end">
      <button type="button" class="vc-link" (click)="toForm()">I'll fill it in</button>
    </span>
    <span class="vc-hint">{{ canSpeak ? micHint : 'Tap or type your answer' }}</span>
  </div>

  <!-- "Shall I create it?" — Create moves in here, so the screen still has one red. -->
  <div *ngIf="isRecap" class="vc-recap">
    <button type="button" class="vc-cta" (click)="create()">Create event</button>
    <div class="vc-recap-row">
      <button type="button" class="vc-link" (click)="toForm()">Change something</button>
      <button *ngIf="canSpeak" type="button" class="vc-link" [disabled]="phase === 'thinking'" (click)="tapMic()">
        {{ phase === 'listening' ? 'Listening… tap to stop' : 'Or say “yes”' }}
      </button>
    </div>
  </div>
</section>

<!-- Solar microphone-2-linear -->
<ng-template #micIcon24>
  <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
    <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.5">
      <path d="M7 8C7 5.23858 9.23858 3 12 3C14.7614 3 17 5.23858 17 8V11C17 13.7614 14.7614 16 12 16C9.23858 16 7 13.7614 7 11V8Z" />
      <path d="M13.5 8L17 8" /><path d="M13.5 11L17 11" /><path d="M7 8L9 8" /><path d="M7 11L9 11" />
      <path d="M20 10V11C20 15.4183 16.4183 19 12 19C7.58172 19 4 15.4183 4 11V10" />
      <path d="M12 19V22" />
    </g>
  </svg>
</ng-template>
<ng-template #micIcon16>
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.8">
      <path d="M7 8C7 5.23858 9.23858 3 12 3C14.7614 3 17 5.23858 17 8V11C17 13.7614 14.7614 16 12 16C9.23858 16 7 13.7614 7 11V8Z" />
      <path d="M20 10V11C20 15.4183 16.4183 19 12 19C7.58172 19 4 15.4183 4 11V10" />
      <path d="M12 19V22" />
    </g>
  </svg>
</ng-template>
